import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

interface Struttura {
  nome: string;
  id_responsabile: number | null;
  descrizione: string | null;
  responsabile_username?: string;
  responsabile_nome?: string;
  responsabile_cognome?: string;
}

interface Utente {
  id: number;
  username: string;
  nome: string;
  cognome: string;
  enabled: boolean;
}

const StrutturePage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [strutture, setStrutture] = useState<Struttura[]>([]);
  const [utenti, setUtenti] = useState<Utente[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStruttura, setEditingStruttura] = useState<Struttura | null>(null);
  const [formData, setFormData] = useState<Partial<Struttura>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStrutture();
    loadUtenti();
  }, []);

  const loadStrutture = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/strutture`, { headers });
      if (!response.ok) {
        console.error('Failed to load strutture');
        setStrutture([]);
        setError('Errore nel caricamento delle strutture');
        return;
      }
      const data = await response.json();
      setStrutture(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading strutture:', err);
      setStrutture([]);
      setError('Errore nel caricamento delle strutture');
    }
  };

  const loadUtenti = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/utenti`, { headers });
      if (!response.ok) {
        console.error('Failed to load utenti');
        setUtenti([]);
        return;
      }
      const data = await response.json();
      setUtenti(Array.isArray(data) ? data.filter((u: Utente) => u.enabled !== false) : []);
    } catch (err) {
      console.error('Error loading utenti:', err);
      setUtenti([]);
    }
  };

  const handleOpenDialog = (struttura?: Struttura) => {
    if (struttura) {
      setEditingStruttura(struttura);
      setFormData(struttura);
    } else {
      setEditingStruttura(null);
      setFormData({});
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStruttura(null);
    setFormData({});
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.nome) {
        setError('Nome struttura obbligatorio');
        return;
      }

      const url = editingStruttura
        ? `${BASE_URL}/strutture/${editingStruttura.nome}`
        : `${BASE_URL}/strutture`;

      const method = editingStruttura ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nel salvataggio');
      }

      loadStrutture();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (nome: string) => {
    if (!window.confirm(`Eliminare la struttura "${nome}"?`)) return;

    try {
      const response = await fetch(`${BASE_URL}/strutture/${nome}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nell\'eliminazione');
      }

      loadStrutture();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Gestione Strutture
        </Typography>
        {hasPermission('strutture', 'create') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nuova Struttura
          </Button>
        )}
      </Box>

      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Responsabile</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Descrizione</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {strutture.map((struttura) => (
              <TableRow key={struttura.nome} hover>
                <TableCell>{struttura.nome}</TableCell>
                <TableCell>
                  {struttura.responsabile_nome && struttura.responsabile_cognome
                    ? `${struttura.responsabile_cognome} ${struttura.responsabile_nome}`
                    : '-'}
                </TableCell>
                <TableCell>{struttura.descrizione || '-'}</TableCell>
                <TableCell>
                  {hasPermission('strutture', 'update') && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(struttura)}
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {hasPermission('strutture', 'delete') && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(struttura.nome)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog per creazione/modifica */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStruttura ? 'Modifica Struttura' : 'Nuova Struttura'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Nome Struttura"
            type="text"
            fullWidth
            required
            value={formData.nome || ''}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            disabled={!!editingStruttura}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel>Responsabile</InputLabel>
            <Select
              value={formData.id_responsabile || ''}
              label="Responsabile"
              onChange={(e) => setFormData({ ...formData, id_responsabile: e.target.value as number || null })}
            >
              <MenuItem value="">
                <em>Nessuno</em>
              </MenuItem>
              {utenti.map((utente) => (
                <MenuItem key={utente.id} value={utente.id}>
                  {utente.cognome} {utente.nome} ({utente.username})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Descrizione"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={formData.descrizione || ''}
            onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StrutturePage;
